import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Select,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  InputAdornment,
  Alert,
  IconButton,
} from "@mui/material";
import { Close, Save, Cancel, Print, AccountBox } from "@mui/icons-material";

const DynamicFormModal = ({
  open,
  onClose,
  title,
  sections = [],
  initialForm = {},
  data = null,
  onSave,
  context = {},
  optionData = [],
  validateForm,
  mapFormValues,
  saveButtonText,

  cardSx,
  children
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const defaultCardSx = {
    height: 'fit-content',
    width: '100%',
    backgroundColor: theme.palette.mode === 'dark' ? `${theme.palette.primary.main}08` : `${theme.palette.primary.main}0A`,
    borderTop: `3px solid ${theme.palette.primary.main}66`
  };

  useEffect(() => {
    if (open && data) {
         setForm({ ...initialForm, ...data });
    } else {
        setForm(initialForm);
    }
    setErrors({});
  }, [open, data, initialForm]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error for field on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = () => {
    if (validateForm) {
        const validationErrors = validateForm(form);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
    }

    let formData = { ...form };
    if (mapFormValues) {
        formData = mapFormValues(formData, optionData);
    }

    onSave(formData);
    onClose();
  };

  const renderField = (field) => {
    const commonProps = {
      fullWidth: true,
      size: "small",
      name: field.name,
      label: field.label,
      value: field.value !== undefined ? (typeof field.value === "function" ? field.value(form) : field.value) : (form[field.name] ?? ""),
      disabled: field.disabled || (typeof field.disabled === 'function' && field.disabled(form)) || false,
      error: !!errors[field.name],
      sx: { backgroundColor: field.backgroundColor, ...field.sx },
    };

    const helperText = errors[field.name] || (typeof field.helper === "function" ? field.helper(form) : field.helper);

    if (field.type === "select") {
      const options = field.options || 
        (optionData.find((opt) => opt.optionName === field.optionName)?.values || []);
      
      return (
        <TextField
          {...commonProps}
          select
          onChange={(e) => {
            handleChange(e);
            if (field.onChange) {
              field.onChange(e, { form, setForm, ...context });
            }
          }}
          onBlur={(e) => {
            if (field.onBlur) {
              field.onBlur(e, { form, setForm, field, ...context });
            }
          }}
          helperText={helperText}
        >
          <MenuItem value="" disabled>
            <em>{field.placeholder || field.label}</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem
              key={opt.id || opt.value || opt.optionValue || opt}
              value={opt.value || opt.optionValue || opt}
            >
              {opt.label || opt.optionText || opt}
            </MenuItem>
          ))}
        </TextField>
      );
    } else if (field.type === "checkbox") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              name={field.name}
              checked={!!form[field.name]}
              onChange={handleChange}
              disabled={commonProps.disabled}
              sx={{ backgroundColor: field.backgroundColor }}
            />
          }
          label={field.label}
        />
      );
    } else if (field.type === "custom") {
      return field.render(field, form, handleChange);
    } else {
      // Default / Text / Number / Date
      return (
        <TextField
          {...commonProps}
          type={field.type || "text"}
          multiline={field.multiline || false}
          rows={field.rows || 1}
          onChange={(e) => {
            handleChange(e);
            if (field.onChange) {
              field.onChange(e, { form, setForm, ...context });
            }
          }}
          onBlur={(e) => {
            if (field.onBlur) {
              field.onBlur(e, { form, setForm, field, ...context });
            }
          }}
          helperText={helperText}
          InputLabelProps={{
            shrink: (field.type === "date" || field.type === "datetime-local" || field.type === "time" || !!form[field.name]) ? true : undefined
          }}
          InputProps={{
            startAdornment: field.adornment && (
              <InputAdornment position="start">
                {typeof field.adornment === "function"
                  ? field.adornment(form)
                  : field.adornment}
              </InputAdornment>
            ),
            endAdornment: field.endAdornment && (
              <InputAdornment position="end">
                {typeof field.endAdornment === "function"
                  ? field.endAdornment(form)
                  : field.endAdornment}
              </InputAdornment>
            ),
            inputProps: { min: field.min || 0, readOnly: field.readOnly || false },
          }}
        />
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: 2, width: "95%", maxWidth: "1400px" },
      }}
    >
      {/* --- HEADER --- */}
      <DialogTitle
        sx={{
            display: "flex",
            justifyContent: "space-between", // Keep existing
            alignItems: "center",
            pb: 1,
            px: { xs: 1, sm: 2 },
            pt: { xs: 1, sm: 2 },
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.primary.dark
                : theme.palette.primary.main,
            color: "white",
            borderRadius: isMobile ? 0 : "16px 16px 0 0",
          }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AccountBox />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* --- CONTENT --- */}
      <DialogContent dividers sx={{ py: 3 }}>
        {Object.keys(errors).length > 0 && (
            <Box sx={{ mb: 2 }}>
                <Alert severity="error">
                    Please correct the errors below.
                </Alert>
            </Box>
        )}
        {/* ✅ Use Box flex layout to stretch to full width */}
        <Grid container spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
          {sections.map((section, sIdx) => (
            <Grid
              item
              key={sIdx}
              size={section.gridSize || { xs: 12 }}
              sx={{ display: "flex" }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1, // ✅ ensures it stretches fully
                  minHeight: section.height ? `${section.height}px` : "auto",
                  opacity: section.disabled && typeof section.disabled === 'function' && section.disabled(form) ? 0.5 : 1,
                  pointerEvents: section.disabled && typeof section.disabled === 'function' && section.disabled(form) ? 'none' : 'auto',
                  ...defaultCardSx,
                  ...(cardSx || {}),
                  ...(section.cardSx || {}),
                }}
              >
                <CardHeader
                  title={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" gutterBottom sx={{ mb: 2, color: theme.palette.primary.main, ...(section.titleSx || {}) }}>
                        {section.title}
                      </Typography>

                      {/* 🟢 Auto-generated buttons */}
                      {section.buttons && !section.inlineWithLabel && (
                        <Box display="flex" gap={1}>
                          {section.buttons.map((btn, i) => {
                            const Icon =
                              btn.icon === "Edit"
                                ? require("@mui/icons-material").Edit
                                : btn.icon === "Search"
                                  ? require("@mui/icons-material").Search
                                  : btn.icon === "Refresh"
                                    ? require("@mui/icons-material").Refresh
                                    : btn.icon === "Print"
                                      ? require("@mui/icons-material").Print
                                      : null;

                            return (
                              <Button
                                key={i}
                                size="small"
                                variant="contained"
                                color={btn.color || "primary"}
                                startIcon={Icon ? React.createElement(Icon) : null}
                                onClick={btn.onClick}
                                disabled={btn.disabled}
                              >
                                {btn.label}
                              </Button>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  }
                  sx={{ backgroundColor: "transparent", pb: 0 }}
                />

                <CardContent sx={{ pt: 1, pb: 2 }}>
                  {section.inlineWithLabel ? (
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        {section.fields.map((field, fIdx) => (
                           <Box key={fIdx} sx={{ width: field.sx?.width || 'auto', minWidth: '200px' }}>
                             {renderField(field)}
                           </Box>
                        ))}
                      </Box>
                      {section.buttons && (
                        <Box display="flex" gap={1} sx={{ mt: { xs: 2, md: 0 } }}>
                          {section.buttons.map((btn, i) => (
                            <Button
                              key={i}
                              size="small"
                              variant="contained"
                              color={btn.color || "primary"}
                              onClick={btn.onClick}
                            >
                              {btn.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Grid container spacing={1}>
                      {section.fields.map((field, fIdx) => (
                        <Grid
                          key={fIdx}
                          item
                          size={field.size || 12} 
                          xs={typeof field.size === 'object' ? field.size.xs : (typeof field.size === 'number' ? field.size : 12)}
                          sm={typeof field.size === 'object' ? field.size.sm : undefined}
                          md={typeof field.size === 'object' ? field.size.md : undefined}
                          lg={typeof field.size === 'object' ? field.size.lg : undefined}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {renderField(field)}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      {/* --- ACTIONS --- */}
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<Print />}
          onClick={() => console.log("Voucher Print clicked")}
        >
          Voucher Print
        </Button>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Cancel />}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={handleSubmit}
          >
            {saveButtonText || "Save"}
          </Button>
        </Box>
      </DialogActions>

      
      {/* Render children (popup or other auxiliary components) */}
      {typeof children === 'function' ? children({ form, setForm, errors, setErrors }) : children}
    </Dialog>
  );
};

export default DynamicFormModal;
